// React-side counterpart to permission-watch.ts. The DOM scanner
// (accessManager.bundle.js) only ever hides/disables the DOM node it's
// attached to — it never renders a substitute message, so a "hidden"
// feature just goes blank. This hook fetches the same IAM permission-matrix
// endpoint directly so components can render an explicit "restricted"
// message instead, and so permission checks can drive plain React logic
// (e.g. filtering <option> elements) where the DOM scanner can't reach.
import { useEffect, useState } from 'react';
import axios from 'axios';
import { readAccessToken } from '@/utils/auth-token-store';
const DEFAULT_FLAGS = {
    allow: true,
    readonly: false,
    masked: false,
    restricted: false,
    hidden: false,
};
let matrixPromise = null;
function getAccessToken() {
    if (typeof window === 'undefined')
        return '';
    try {
        // localStorage stores this JSON-quoted (same as PlanManagementWidget's
        // getAccessToken) — an unstripped token fails auth against the matrix API.
        return readAccessToken() || '';
    }
    catch (_a) {
        return '';
    }
}
function fetchMatrix() {
    if (matrixPromise)
        return matrixPromise;
    const permissionApiUrl = process.env.NEXT_PUBLIC_IAM_ROUTE
        ? `${process.env.NEXT_PUBLIC_IAM_ROUTE}/permission-matrix`
        : undefined;
    if (!permissionApiUrl) {
        matrixPromise = Promise.resolve({});
        return matrixPromise;
    }
    matrixPromise = axios
        .get(permissionApiUrl, {
        headers: {
            Authorization: `Bearer ${getAccessToken()}`,
            'app-identifier': process.env.NEXT_PUBLIC_IAM_APP_IDENTIFIER,
            'customer-secret-key': process.env.NEXT_PUBLIC_IAM_SECRET_KEY,
        },
    })
        .then((res) => {
        var _a, _b, _c;
        // Real shape: { data: { permissionMetrics: [...] } } — not data itself.
        const rows = Array.isArray((_b = (_a = res.data) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.permissionMetrics)
            ? res.data.data.permissionMetrics
            : Array.isArray((_c = res.data) === null || _c === void 0 ? void 0 : _c.data)
                ? res.data.data
                : Array.isArray(res.data)
                    ? res.data
                    : [];
        const map = {};
        rows.forEach((row) => {
            const code = row === null || row === void 0 ? void 0 : row.feature_code;
            if (!code)
                return;
            map[code] = {
                allow: !!row.allow_flag,
                readonly: !!row.read_flag,
                masked: !!row.mask_flag,
                restricted: !!row.restrict_flag,
                hidden: !!row.hidden_flag,
                restrictedMessage: row.restrict_message,
                maskedMessage: row.mask_message,
            };
        });
        return map;
    })
        .catch((err) => {
        // Don't cache a failed fetch — a transient blip would otherwise
        // permanently fail every feature open (allow: true) for the rest of
        // the page session, since matrixPromise is a module-level singleton.
        matrixPromise = null;
        throw err;
    });
    return matrixPromise;
}
// Resolves the permission flags for a single feature code. While the
// matrix is still loading, `allow: true` is assumed so content isn't
// flashed as restricted before the real answer arrives.
export function usePermission(featureCode) {
    const [matrix, setMatrix] = useState(null);
    useEffect(() => {
        let alive = true;
        fetchMatrix().then((m) => {
            if (alive)
                setMatrix(m);
        }, () => {
            // Fail open for this mount only — fetchMatrix already cleared the
            // shared cache above, so the next component to mount retries fresh.
            if (alive)
                setMatrix({});
        });
        return () => {
            alive = false;
        };
    }, []);
    if (!featureCode || !matrix)
        return Object.assign(Object.assign({}, DEFAULT_FLAGS), { loading: !matrix });
    return Object.assign(Object.assign({}, (matrix[featureCode] || DEFAULT_FLAGS)), { loading: false });
}
export default usePermission;
